import React, { useState, useRef, useEffect, FC } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IoIosArrowDown, IoMdClose } from "react-icons/io";
import { RxHamburgerMenu } from "react-icons/rx";
import { HiOutlineBell } from "react-icons/hi2";
import { Badge } from "antd";
import { useNotificationStore, useProfileStore } from "store";
import { NotificationDropdown } from "dashboard/ui/base/NotificationDropdown";
import { routeNames } from "utils";

interface MenuItemProps {
  active?: boolean;
}

const Nav = styled(motion.nav)`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
  font-family: "Manrope", sans-serif;
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const NavContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1440px;
  margin: 0 auto;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const Logo = styled(motion.img)`
  height: 32px;
`;

const NavigationLinks = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(motion(Link))<MenuItemProps>`
  text-decoration: none;
  font-size: 14px;
  color: ${(props) => (props.active ? "#5575F7" : "#6B7280")};
  font-weight: ${(props) => (props.active ? "500" : "400")};
  transition: color 0.2s;

  &:hover {
    color: #5575f7;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownIndicator = styled(motion.span)`
  margin-left: 5px;
  font-size: 10px;
  margin-top: 3px;
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  background-color: white;
  min-width: 160px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 0.5rem 0;
  z-index: 1001;
`;

const DropdownItem = styled(motion.div)`
  padding: 0.5rem 1rem;
  color: #000000;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background-color: #f5f5f5;
    color: #5575f7;
  }
`;

const UserDropdownMenu = styled(DropdownMenu)`
  right: 0;
  left: auto;
  min-width: 200px;
`;

const UserDropdownItem = styled(DropdownItem)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LanguageSelector = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 14px;
  color: #6b7280;
  padding: 0.25rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NotificationButton = styled(motion.button)`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #5575f7;
  }
`;

const UserInitials = styled(motion.div)`
  width: 32px;
  height: 32px;
  background: #5575f7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-weight: 500;
  font-family: "Manrope", sans-serif;
`;

const UserMenu = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
  }
`;

const MobileMenu = styled(motion.div)`
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
`;

export interface NavbarProps {
  onLogout: () => void;
}
export const Navbar:FC<NavbarProps> = ({
  onLogout
}) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const navigate = useNavigate();

  const languageRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const languages = ["English", "Español", "Français"];
  const { isOpen, setIsOpen, notifications } = useNotificationStore();
  const unreadCount = notifications.filter((n) => !n.read).length;

  const { user, getInitials, isSuperAdmin, isCourseAdmin, isUserAdmin } = useProfileStore();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        languageRef.current &&
        !languageRef.current.contains(event.target as Node)
      ) {
        setIsLanguageOpen(false);
      }
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 },
    },
  };

  const dropdownVariants = {
    hidden: {
      opacity: 0,
      y: -10,
      height: 0,
    },
    visible: {
      opacity: 1,
      y: 0,
      height: "auto",
      transition: {
        duration: 0.3,
        staggerChildren: 0.05,
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      height: 0,
      transition: {
        duration: 0.2,
      },
    },
  };

  const dropdownItemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.2 },
    },
  };

  const mobileMenuVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        when: "afterChildren",
      },
    },
  };

  return (
    <Nav initial="hidden" animate="visible" variants={navVariants}>
      <NavContainer>
        <LeftSection>
          <Link to={"#"} onClick={() => window.location.href = routeNames.home}>
            <Logo
              src="/assets/logo.png"
              alt="compellingcandidature"
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            />
          </Link>
          <NavigationLinks variants={itemVariants}>
            {(isUserAdmin || isSuperAdmin) && <NavLink
              to={routeNames.adminUsers}
              active={isActive(routeNames.adminUsers)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Users
            </NavLink>}
            {(isUserAdmin || isSuperAdmin) && <NavLink
              to={routeNames.adminAdminUsers}
              active={isActive(routeNames.adminAdminUsers)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Admin Users
            </NavLink>}
            {(isSuperAdmin || isCourseAdmin) && <NavLink
              to={routeNames.adminCourses}
              active={isActive(routeNames.adminCourses)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Course Management
            </NavLink>}
            {(isSuperAdmin || isCourseAdmin) && <NavLink
              to={routeNames.adminInterviews}
              active={isActive(routeNames.adminInterviews)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Interview Management
            </NavLink>}
          </NavigationLinks>
        </LeftSection>

        <RightSection>
          <ActionButtons>
            {/* <DropdownContainer ref={languageRef}>
              <LanguageSelector
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>English</span>
                <DropdownIndicator
                  animate={{ rotate: isLanguageOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <IoIosArrowDown size={16} />
                </DropdownIndicator>
              </LanguageSelector>

              <AnimatePresence>
                {isLanguageOpen && (
                  <DropdownMenu
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    {languages.map((lang, index) => (
                      <DropdownItem
                        key={index}
                        variants={dropdownItemVariants}
                        whileHover={{ x: 5 }}
                      >
                        {lang}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                )}
              </AnimatePresence>
            </DropdownContainer> */}

            <NotificationButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
            >
              <Badge count={unreadCount} size="small">
                <HiOutlineBell size={20} />
              </Badge>
              <NotificationDropdown />
            </NotificationButton>

            <DropdownContainer ref={userMenuRef}>
              <UserMenu
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <UserInitials>{getInitials()}</UserInitials>
                <div>{user?.lastName || ""} {user?.firstName || ""}</div>
                <DropdownIndicator
                  animate={{ rotate: isUserMenuOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <IoIosArrowDown size={16} color="#6B7280" />
                </DropdownIndicator>
              </UserMenu>

              <AnimatePresence>
                {isUserMenuOpen && (
                  <UserDropdownMenu
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <UserDropdownItem
                      variants={dropdownItemVariants}
                      whileHover={{ x: 5 }}
                      style={{ color: "#EF4444" }}
                      onClick={onLogout}
                    >
                      Sign Out
                    </UserDropdownItem>
                  </UserDropdownMenu>
                )}
              </AnimatePresence>
            </DropdownContainer>

            <MobileMenuButton
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {isMobileMenuOpen ? <IoMdClose /> : <RxHamburgerMenu />}
            </MobileMenuButton>
          </ActionButtons>
        </RightSection>
      </NavContainer>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <NavLink to={routeNames.adminUsers} active={isActive(routeNames.adminUsers)}>
              Users
            </NavLink>
            <NavLink to={routeNames.adminAdminUsers} active={isActive(routeNames.adminAdminUsers)}>
              Admin Users
            </NavLink>
            <NavLink to={routeNames.adminCourses} active={isActive(routeNames.adminCourses)}>
              Course Management
            </NavLink>
            <NavLink to={routeNames.adminInterviews} active={isActive(routeNames.adminInterviews)}>
              Interview Management
            </NavLink>
            {/* <LanguageSelector>
              <span>English</span>
              <IoIosArrowDown size={16} />
            </LanguageSelector> */}
          </MobileMenu>
        )}
      </AnimatePresence>
    </Nav>
  );
};
